import React, { Fragment, useState } from 'react';
import { withPagination } from '../../../../../hoc';
import {
    ItemsPerPage,
    Pagination,
    SectionTitle,
    Table,
} from '../../../../../components';
import styles from './index.module.css';
import round from '../../../../../utils/roundNumber';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faTimesCircle,
} from '@fortawesome/free-regular-svg-icons';
import CorrectDepth from '../CorrectDepth';
import useDeepCompareEffect from 'use-deep-compare-effect';

const SinglePileCapacityPN = ({
    dataToPaginate,
    allCollection = [],
    pagination,
    paginationHelpers,
}) => {
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [csvData, setCsvData] = useState([]);

    useDeepCompareEffect(() => {
        setCsvData(
            allCollection
                .map(
                    ({
                        z,
                        d,
                        np,
                        ns,
                        ns1,
                        tn,
                        tn1,
                        nt,
                        ntw,
                        nw,
                        nw1,
                        capacityParameters: { m },
                    }) => ({ z, d, np, ns, ns1, tn, tn1, nt, ntw, nw, nw1, m }),
                )
                .map((ele) =>
                    Object.fromEntries(
                        Object.entries(ele).map(([key, value = '']) => [
                            [key],
                            value.toString().replace('.', ','),
                        ]),
                    ),
                ),
        );

        setCsvHeaders([
            {
                label: 'Z [m.p.p.t.]',
                key: 'z',
            },
            {
                label: 'd [m]',
                key: 'd',
            },
            {
                label: 'Np [kN]',
                key: 'np',
            },
            {
                label: 'Ns1 [kN]',
                key: 'ns1',
            },
            {
                label: 'Ns [kN]',
                key: 'ns',
            },
            {
                label: 'Nw1 [kN]',
                key: 'nw1',
            },
            {
                label: 'Nw [kN]',
                key: 'nw',
            },
            {
                label: 'Tn1 [kN]',
                key: 'tn1',
            },
            {
                label: 'Tn [kN]',
                key: 'tn',
            },
            {
                label: 'm [-]',
                key: 'm',
            },
            {
                label: 'm x Nt [kN]',
                key: 'nt',
            },
            {
                label: 'm x Ntw [kN]',
                key: 'ntw',
            },
        ]);
    }, [allCollection]);

    return (
        <Fragment>
            <SectionTitle
                title={
                    'Nośność pionowa pala pojedynczego wg polskiej normy - PN-83/B-2482'
                }
            />
            <ItemsPerPage
                onDisplayItemsNumber={paginationHelpers.displayItemsNumber}
                itemsPerPage={pagination.itemsPerPage}
                totalItems={paginationHelpers.allItemsLength}
                display={[15, 30, paginationHelpers.allItemsLength]}
            />
            <Table
                csvData={csvData}
                csvFilename={'SinglePileCapacityPN'}
                csvHeaders={csvHeaders}>
                <thead>
                    <tr>
                        <th>
                            Nr<br></br>[-]
                        </th>
                        <th title="Rzędna">
                            z<br></br>[m.p.p.t.]
                        </th>
                        <th title="Nośność podstawy">
                            N<sub>p</sub>
                            <br></br>[kN]
                        </th>
                        <th title="Przyrost nośności pobocznicy">
                            N<sub>s1</sub>
                            <br></br>[kN]
                        </th>
                        <th title="Całkowita nośność pobocznicy">
                            N<sub>s</sub>=&Sigma;N<sub>s1</sub>
                            <br></br>[kN]
                        </th>
                        <th title="Przyrost nośności na wyciąganie">
                            N<sub>w1</sub>
                            <br></br>[kN]
                        </th>
                        <th title="Całkowita nośność na wyciąganie">
                            N<sub>w</sub>=&Sigma;N<sub>w1</sub>
                            <br></br>[kN]
                        </th>
                        <th title="Przyrost tarcia negatywnego">
                            T<sub>n1</sub>
                            <br></br>[kN]
                        </th>
                        <th title="Całkowite tarcie negatywne">
                            T<sub>n</sub>=&Sigma;T<sub>n1</sub>
                            <br></br>[kN]
                        </th>
                        <th title="Współczynnik modelu">
                            m<br></br>[-]
                        </th>
                        <th title="Nośność obliczeniowa na wciskanie">
                            m &#215; N<sub>t</sub>
                            <br></br>[kN]
                        </th>
                        <th title="Nośność obliczeniowa na wyciąganie">
                            m &#215; N<sub>tw</sub>
                            <br></br>[kN]
                        </th>
                        <th title="Poprawność zagłębienia pala w gruncie"></th>
                    </tr>
                </thead>
                <tbody className={styles.tbody}>
                    {dataToPaginate.map((element, index) => {
                        const {
                            correctBaseDepth,
                            correctMultiLayerDepth,
                            isCohesive,
                            isSoftLayer,
                        } = element.correctDepth;
                        const correctLength =
                            correctBaseDepth >= 1 &&
                            correctMultiLayerDepth &&
                            !isCohesive &&
                            !isSoftLayer;
                        const warningDepth =
                            correctBaseDepth > 0 &&
                            correctBaseDepth < 1 &&
                            correctMultiLayerDepth &&
                            !isCohesive &&
                            !isSoftLayer;
                        return (
                            <tr
                                className={`${styles.tr} ${
                                    correctLength
                                        ? styles.validDepth
                                        : warningDepth
                                        ? styles.warningDepth
                                        : styles.wrongDepth
                                }`}
                                key={index}>
                                <td>
                                    {paginationHelpers.countingItemsPerPage +
                                        index +
                                        1}
                                </td>
                                <td>{round(element.z, 2)}</td>
                                <td>{round(element.np, 2)}</td>
                                <td>{round(element.ns1, 2)}</td>
                                <td>{round(element.ns, 2)}</td>
                                <td>{round(element.nw1, 2)}</td>
                                <td>{round(element.nw, 2)}</td>
                                <td>{round(element.tn1, 2)}</td>
                                <td>{round(element.tn, 2)}</td>
                                <td>
                                    {round(element.capacityParameters.m, 2)}
                                </td>
                                <td>{round(element.nt, 2)}</td>
                                <td>{round(element.ntw, 2)}</td> 
                                <td>
                                    <FontAwesomeIcon
                                        icon={
                                            correctLength
                                                ? faCheckCircle
                                                : warningDepth
                                                ? faCheckCircle
                                                : faTimesCircle
                                        }
                                    />
                                    <div className={styles.info}>
                                        <CorrectDepth
                                            className={styles.depthColor}
                                            correctDepth={element.correctDepth}
                                        />
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            <Pagination
                currentPage={pagination.currentPage}
                itemsPerPage={pagination.itemsPerPage}
                totalItems={paginationHelpers.allItemsLength}
                nextPage={paginationHelpers.nextPage}
            />
        </Fragment>
    );
};

export default withPagination({
    component: SinglePileCapacityPN,
    returnAllCollection: true,
    paginationState: {
        currentPage: 1,
        itemsPerPage: 25,
    },
});